// H3 PR 2 — the session JWT lives only in the httpOnly cookie `miridar_session` set by the backend, so a script
// injected in the page cannot read it. The page keeps a non-secret session marker (`user-session`) with what the UI
// needs: the user, the company and when the session ends. A `user-token` left by a login made before this change is
// still honoured (main.js sends it as Bearer) until it expires; new logins never write it.
// Every reader of "is the user signed in / who is it" goes through this module, never through the JWT.

export const SESSION_MARKER_KEY = "user-session";
export const LEGACY_TOKEN_KEY = "user-token";
const SESSION_FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

const parsePositiveId = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.trunc(numeric);
};

const readItem = (storage, key) => {
  try {
    return storage?.getItem?.(key) || null;
  } catch (_) {
    return null;
  }
};

export const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3 || typeof globalThis?.atob !== "function") return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
    const payload = JSON.parse(globalThis.atob(padded));
    return payload && typeof payload === "object" ? payload : null;
  } catch (_) {
    return null;
  }
};

export const readLegacyToken = (storage = globalThis?.localStorage) => readItem(storage, LEGACY_TOKEN_KEY);

// Written once per successful login/registration/Google session response.
export const writeSessionMarker = (payload = {}, { storage = globalThis?.localStorage, now = Date.now() } = {}) => {
  const expiresAt = Date.parse(payload.sessionExpiresAt);
  const marker = {
    userId: parsePositiveId(payload.userId),
    companyId: parsePositiveId(payload.companyId),
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : now + SESSION_FALLBACK_TTL_MS,
    issuedAt: now,
  };
  storage?.setItem?.(SESSION_MARKER_KEY, JSON.stringify(marker));
  return marker;
};

const readLegacySession = (legacyToken) => {
  const payload = decodeJwtPayload(legacyToken);
  if (!payload) return { invalid: true };
  const exp = Number(payload.exp);
  const userId = parsePositiveId(payload.userId ?? payload.id);
  return {
    userId,
    companyId: parsePositiveId(payload.companyId ?? payload.company_id ?? payload.companyID),
    expiresAt: Number.isFinite(exp) && exp > 0 ? exp * 1000 : null,
    key: `legacy:${userId || 0}:${Number(payload.iat) || 0}`,
    tokenPayload: payload,
  };
};

// The session as the UI sees it: null when signed out, `{ invalid: true }` when the stored state is unusable and
// must be cleared, otherwise `{ userId, companyId, expiresAt, key, tokenPayload }`. `key` identifies this login
// (Home's one-time default redirect); `tokenPayload` is set only for a legacy pre-H3 token.
// A legacy token wins over a marker: a new login always deletes it, so both exist only when a tab still running the
// pre-deploy bundle logged in after the marker was written — and requests then run as that token (Bearer first).
export const readAuthSession = (storage = globalThis?.localStorage) => {
  const legacyToken = readLegacyToken(storage);
  if (legacyToken) return readLegacySession(legacyToken);
  const rawMarker = readItem(storage, SESSION_MARKER_KEY);
  if (!rawMarker) return null;
  try {
    const marker = JSON.parse(rawMarker);
    const expiresAt = Number(marker?.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return { invalid: true };
    return {
      userId: parsePositiveId(marker.userId),
      companyId: parsePositiveId(marker.companyId),
      expiresAt,
      key: `session:${Number(marker.issuedAt) || expiresAt}`,
      tokenPayload: null,
    };
  } catch (_) {
    return { invalid: true };
  }
};

export const isAuthSessionExpired = (session, now = Date.now()) =>
  Boolean(session?.expiresAt) && now >= session.expiresAt;

export const hasAuthSession = (storage = globalThis?.localStorage, now = Date.now()) => {
  const session = readAuthSession(storage);
  return Boolean(session) && !session.invalid && !isAuthSessionExpired(session, now);
};

// Responses that mean the backend no longer accepts this browser's session: an expired token (any page, as
// before), a revoked session, or a request that reached `verifyToken` without any credential (cookie dropped).
// The last two end the session only when the page believes it is signed in or shows a protected route (a logout
// in another tab already removed the shared marker), so public pages are left alone.
export const isSessionEndResponse = (response, { signedIn = false, protectedRoute = false } = {}) => {
  const status = Number(response?.status);
  const data = response?.data;
  const message = typeof data === "string" ? data : typeof data?.message === "string" ? data.message : "";
  if (status === 401 && message.toLowerCase().includes("expired")) return true;
  if (!signedIn && !protectedRoute) return false;
  if (status === 401 && data?.code === "SESSION_REVOKED") return true;
  return status === 403 && message === "Tokenul este necesar pentru autentificare.";
};
